import React, { useState, useEffect, useRef } from 'react';
import SignaturePad from './SignaturePad';
import {
  SALON_POLICIES,
  RISKS_AND_CONTRAINDICATIONS,
  SERVICE_TO_RISKS_MAP,
  GUEST_INTAKE_CONSENT,
  BROW_LAM_INTAKE_CONSENT,
  BROW_LAM_CONSENT_INTRO,
  BROW_LAM_CONSENT_STATEMENTS,
  BROW_LAM_CONTRAINDICATION_LIST,
  BROW_LAM_FINAL_CONSENT
} from './formContent';
import './FormRenderer.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─── FIELD COMPONENTS ────────────────────────────────────────────

function TextField({ field, value, onChange, error }) {
  return (
    <div className={`fr-field ${field.halfWidth ? 'fr-half' : ''}`}>
      <label className="fr-label">
        {field.label}{field.required && <span className="field-required"> *</span>}
      </label>
      <input
        type={field.inputType || 'text'}
        className={`fr-input ${error ? 'fr-input-error' : ''}`}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={field.placeholder || ''}
        readOnly={field.readOnly}
      />
      {error && <div className="fr-error">{error}</div>}
    </div>
  );
}

function TextArea({ field, value, onChange, error }) {
  return (
    <div className="fr-field">
      <label className="fr-label">
        {field.label}{field.required && <span className="field-required"> *</span>}
      </label>
      <textarea
        className={`fr-input fr-textarea ${error ? 'fr-input-error' : ''}`}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={field.placeholder || ''}
        rows={3}
      />
      {error && <div className="fr-error">{error}</div>}
    </div>
  );
}

function DateField({ field, value, onChange, error }) {
  return (
    <div className={`fr-field ${field.halfWidth ? 'fr-half' : ''}`}>
      <label className="fr-label">
        {field.label}{field.required && <span className="field-required"> *</span>}
      </label>
      <input
        type="date"
        className={`fr-input ${error ? 'fr-input-error' : ''}`}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
      />
      {error && <div className="fr-error">{error}</div>}
    </div>
  );
}

function RadioGroup({ field, value, onChange, error }) {
  return (
    <div className="fr-field">
      <label className="fr-label">
        {field.label}{field.required && <span className="field-required"> *</span>}
      </label>
      <div className="fr-radio-group">
        {field.options.map(opt => (
          <label key={opt.value} className={`fr-radio-option ${value === opt.value ? 'selected' : ''}`}>
            <input
              type="radio"
              name={field.key}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
            />
            <span className="fr-radio-circle" />
            <span className="fr-radio-text">{opt.label}</span>
          </label>
        ))}
      </div>
      {field.note && <div className="fr-field-note">{field.note}</div>}
      {error && <div className="fr-error">{error}</div>}
    </div>
  );
}

function CheckboxGroup({ field, value, onChange, error }) {
  const selected = value || {};
  function toggle(optValue) {
    const next = { ...selected, [optValue]: !selected[optValue] };
    onChange(next);
  }
  return (
    <div className="fr-field">
      <label className="fr-label">
        {field.label}{field.required && <span className="field-required"> *</span>}
      </label>
      <div className={`fr-checkbox-group ${field.columns ? 'fr-checkbox-columns' : ''}`}>
        {field.options.map(opt => (
          <label key={opt.value} className={`fr-checkbox-option ${selected[opt.value] ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={!!selected[opt.value]}
              onChange={() => toggle(opt.value)}
            />
            <span className="fr-checkbox-box">{selected[opt.value] ? '\u2713' : ''}</span>
            <span className="fr-checkbox-text">{opt.label}</span>
          </label>
        ))}
      </div>
      {error && <div className="fr-error">{error}</div>}
    </div>
  );
}

function ConsentAcknowledge({ field, value, onChange, error }) {
  return (
    <div className="fr-field">
      <label className={`fr-consent-check ${value ? 'checked' : ''} ${error ? 'fr-consent-error' : ''}`} onClick={() => onChange(!value)}>
        <span className="fr-checkbox-box">{value ? '\u2713' : ''}</span>
        <span className="fr-consent-text">{field.label}</span>
        {field.required && <span className="field-required"> *</span>}
      </label>
      {error && <div className="fr-error">{error}</div>}
    </div>
  );
}

// ─── STATIC CONTENT RENDERERS ────────────────────────────────────

function SalonPoliciesSection() {
  return (
    <div className="fr-section">
      <div className="fr-section-header">Salon Policies</div>
      <div className="fr-policies">
        {Object.values(SALON_POLICIES).map(policy => (
          <div key={policy.title} className="fr-policy">
            <div className="fr-policy-title">{policy.title}</div>
            <div className="fr-policy-text">{policy.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RisksSection({ selectedServices }) {
  const servicesToShow = Object.entries(SERVICE_TO_RISKS_MAP)
    .filter(([svcKey, risksKey]) => risksKey && selectedServices[svcKey])
    .map(([, risksKey]) => RISKS_AND_CONTRAINDICATIONS[risksKey])
    .filter(Boolean);

  if (servicesToShow.length === 0) return null;

  return (
    <div className="fr-section">
      <div className="fr-section-header">Risks and Contraindications</div>
      <div className="fr-risks-content">
        {servicesToShow.map(svc => (
          <div key={svc.title} className="fr-risk-block">
            <div className="fr-risk-title">{svc.emoji} {svc.title}</div>
            <div className="fr-risk-subtitle">Contraindications:</div>
            <ul className="fr-risk-list">
              {svc.contraindications.map((item, i) => (
                <li key={i}>
                  {item.text}{item.bold && <strong>{item.bold}</strong>}{item.after || ''}
                </li>
              ))}
            </ul>
            <div className="fr-risk-subtitle">Risks:</div>
            <ul className="fr-risk-list">
              {svc.risks.map((risk, i) => <li key={i}>{risk}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConsentTextSection({ paragraphs, title }) {
  return (
    <div className="fr-consent-block">
      {title && <div className="fr-consent-title">{title}</div>}
      {paragraphs.map((p, i) => (
        <p key={i} className="fr-consent-paragraph">{p}</p>
      ))}
    </div>
  );
}

// ─── BROW LAM CONSENT SECTION ────────────────────────────────────

function BrowLamConsentSection({ formData, onChange, errors }) {
  return (
    <div className="fr-section">
      <div className="fr-section-header-large">Brow Lamination Consent Form</div>
      <p className="fr-consent-paragraph">{BROW_LAM_CONSENT_INTRO}</p>

      {BROW_LAM_CONSENT_STATEMENTS.map((statement, i) => {
        const key = `consent_${i}`;
        const isLast = i === BROW_LAM_CONSENT_STATEMENTS.length - 1;
        return (
          <div key={key}>
            <ConsentAcknowledge
              field={{ key, label: statement, required: true }}
              value={formData[key]}
              onChange={v => onChange(key, v)}
              error={errors[key]}
            />
            {isLast && (
              <ul className="fr-contraindication-list">
                {BROW_LAM_CONTRAINDICATION_LIST.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      <p className="fr-consent-paragraph fr-consent-final">{BROW_LAM_FINAL_CONSENT}</p>

      <div className="fr-sig-row">
        <SignaturePad
          label="Signature"
          required
          value={formData.consentSignature || ''}
          onChange={v => onChange('consentSignature', v)}
        />
        <DateField
          field={{ key: 'consentDate', label: 'Date', required: true, halfWidth: true }}
          value={formData.consentDate || ''}
          onChange={v => onChange('consentDate', v)}
          error={errors.consentDate}
        />
      </div>
      {errors.consentSignature && <div className="fr-error">{errors.consentSignature}</div>}
    </div>
  );
}

// ─── FORM DEFINITIONS ────────────────────────────────────────────

function renderGuestIntakeForm(formData, onChange, errors, guestInfo) {
  const servicesSelected = formData.servicesReceiving || {};
  const showWaxing = !!servicesSelected.waxing;

  return (
    <>
      <div className="fr-form-title">GUEST INTAKE FORM</div>

      {/* Guest Information */}
      <div className="fr-section">
        <div className="fr-section-header">Guest Information</div>
        <div className="fr-row">
          <TextField field={{ key: 'firstName', label: 'First Name', required: true, halfWidth: true }} value={formData.firstName} onChange={v => onChange('firstName', v)} error={errors.firstName} />
          <TextField field={{ key: 'lastName', label: 'Last Name', required: true, halfWidth: true }} value={formData.lastName} onChange={v => onChange('lastName', v)} error={errors.lastName} />
        </div>
        <div className="fr-row">
          <DateField field={{ key: 'birthday', label: 'Birthday', required: true, halfWidth: true }} value={formData.birthday} onChange={v => onChange('birthday', v)} error={errors.birthday} />
          <TextField field={{ key: 'email', label: 'Email Address', required: true, halfWidth: true, inputType: 'email' }} value={formData.email} onChange={v => onChange('email', v)} error={errors.email} />
        </div>
        <div className="fr-row fr-row-top">
          <CheckboxGroup
            field={{
              key: 'servicesReceiving', label: 'Which services are you receiving today? (Please check all that apply)', required: true,
              options: [
                { value: 'waxing', label: 'Waxing' },
                { value: 'tinting', label: 'Tinting (brows or lashes)' },
                { value: 'lashLift', label: 'Lash Lift' },
                { value: 'browLamination', label: 'Brow Lamination' },
                { value: 'nails', label: 'Nails' },
                { value: 'sprayTan', label: 'Spray Tan' },
                { value: 'laserHairRemoval', label: 'Laser Hair Removal' }
              ]
            }}
            value={formData.servicesReceiving}
            onChange={v => onChange('servicesReceiving', v)}
            error={errors.servicesReceiving}
          />
          <CheckboxGroup
            field={{
              key: 'howDidYouHear', label: 'How did you hear about us?', required: true,
              options: [
                { value: 'advertisement', label: 'Advertisement' },
                { value: 'aiChat', label: 'Ai Chat' },
                { value: 'facebook', label: 'Facebook' },
                { value: 'friendReferral', label: 'Friend Referral' },
                { value: 'instagram', label: 'Instagram' },
                { value: 'internetSearch', label: 'Internet Search' },
                { value: 'signageWalkIn', label: 'Signage / Walk-in' },
                { value: 'tiktok', label: 'TikTok' },
                { value: 'wordOfMouth', label: 'Word of Mouth' },
                { value: 'other', label: 'Other' }
              ]
            }}
            value={formData.howDidYouHear}
            onChange={v => onChange('howDidYouHear', v)}
            error={errors.howDidYouHear}
          />
        </div>
      </div>

      {/* Medical History */}
      <div className="fr-section">
        <div className="fr-section-header">Medical History</div>
        <TextField field={{ key: 'allergies', label: 'Do you have any allergies? If so, please list' }} value={formData.allergies} onChange={v => onChange('allergies', v)} error={errors.allergies} />
        <TextField field={{ key: 'medicalConditions', label: 'Are you currently treated for any medical condition? If so, please list' }} value={formData.medicalConditions} onChange={v => onChange('medicalConditions', v)} error={errors.medicalConditions} />
        <TextField field={{ key: 'medications', label: 'Are you currently taking any medication? If so, please list' }} value={formData.medications} onChange={v => onChange('medications', v)} error={errors.medications} />
        <RadioGroup
          field={{
            key: 'retinol', label: 'Do you use any skincare products containing retinol', required: true,
            options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }],
            note: '*Please note, retinol use should be discontinued at least 5-7 days before face waxing.'
          }}
          value={formData.retinol}
          onChange={v => onChange('retinol', v)}
          error={errors.retinol}
        />

        {showWaxing && (
          <>
            <RadioGroup
              field={{
                key: 'waxedBefore', label: 'Have you waxed before?', required: true,
                options: [
                  { value: 'no', label: 'No' },
                  { value: 'yes', label: 'Yes' },
                  { value: 'firstBrazilian', label: 'Yes, but this is my first Brazilian / bikini or speedo wax' }
                ]
              }}
              value={formData.waxedBefore}
              onChange={v => onChange('waxedBefore', v)}
              error={errors.waxedBefore}
            />
            <CheckboxGroup
              field={{
                key: 'postWaxExperiences', label: 'After a wax, have you ever experienced any of the following?', required: true,
                options: [
                  { value: 'ingrownHair', label: 'Ingrown Hair' },
                  { value: 'breakOut', label: 'Break out or bumps' },
                  { value: 'bruising', label: 'Bruising' },
                  { value: 'bleeding', label: 'Bleeding' },
                  { value: 'burn', label: 'Burn' },
                  { value: 'skinLifting', label: 'Skin Lifting' },
                  { value: 'brokenHair', label: 'Broken Hair' },
                  { value: 'hyperpigmentation', label: 'Hyperpigmentation' },
                  { value: 'none', label: 'None' }
                ]
              }}
              value={formData.postWaxExperiences}
              onChange={v => onChange('postWaxExperiences', v)}
              error={errors.postWaxExperiences}
            />
          </>
        )}
      </div>

      {/* Salon Policies */}
      <SalonPoliciesSection />

      {/* Risks and Contraindications (dynamic) */}
      <RisksSection selectedServices={servicesSelected} />

      {/* Consent and Release */}
      <div className="fr-section">
        <div className="fr-section-header">Consent and Release</div>
        <ConsentTextSection paragraphs={GUEST_INTAKE_CONSENT} />
        <ConsentAcknowledge
          field={{ key: 'agreeTerms', label: 'I agree to these terms and policies', required: true }}
          value={formData.agreeTerms}
          onChange={v => onChange('agreeTerms', v)}
          error={errors.agreeTerms}
        />
        <SignaturePad
          label="Signature"
          required
          value={formData.signature || ''}
          onChange={v => onChange('signature', v)}
        />
        {errors.signature && <div className="fr-error">{errors.signature}</div>}
        <DateField
          field={{ key: 'signatureDate', label: 'Date', required: true }}
          value={formData.signatureDate}
          onChange={v => onChange('signatureDate', v)}
          error={errors.signatureDate}
        />
      </div>
    </>
  );
}

function renderBrowLamForm(formData, onChange, errors, guestInfo) {
  return (
    <>
      <div className="fr-form-title">BROW LAMINATION CLIENT INTAKE FORM</div>

      {/* General Information */}
      <div className="fr-section">
        <div className="fr-section-header">General Information</div>
        <div className="fr-row">
          <TextField field={{ key: 'firstName', label: 'First Name', required: true, halfWidth: true }} value={formData.firstName} onChange={v => onChange('firstName', v)} error={errors.firstName} />
          <TextField field={{ key: 'lastName', label: 'Last Name', required: true, halfWidth: true }} value={formData.lastName} onChange={v => onChange('lastName', v)} error={errors.lastName} />
        </div>
      </div>

      {/* Brow History */}
      <div className="fr-section">
        <div className="fr-section-header">Brow History</div>
        <RadioGroup
          field={{ key: 'browsLaminated', label: 'Have you ever had your brows laminated?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.browsLaminated} onChange={v => onChange('browsLaminated', v)} error={errors.browsLaminated}
        />
        <RadioGroup
          field={{ key: 'tintedBrows', label: 'Have you tinted your eyebrows in the last 6 months?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.tintedBrows} onChange={v => onChange('tintedBrows', v)} error={errors.tintedBrows}
        />
        {formData.tintedBrows === 'yes' && (
          <TextField field={{ key: 'tintMethod', label: 'If yes, what method did you use:' }} value={formData.tintMethod} onChange={v => onChange('tintMethod', v)} error={errors.tintMethod} />
        )}
        {formData.tintedBrows === 'yes' && (
          <>
            <RadioGroup
              field={{ key: 'adverseReaction', label: 'If yes, have you ever had an adverse reaction?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
              value={formData.adverseReaction} onChange={v => onChange('adverseReaction', v)} error={errors.adverseReaction}
            />
            {formData.adverseReaction === 'yes' && (
              <TextField field={{ key: 'adverseReactionExplain', label: 'If yes, please explain:' }} value={formData.adverseReactionExplain} onChange={v => onChange('adverseReactionExplain', v)} error={errors.adverseReactionExplain} />
            )}
          </>
        )}
        <RadioGroup
          field={{ key: 'hairDyeAllergy', label: 'Have you ever had an allergic reaction to hair dye?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.hairDyeAllergy} onChange={v => onChange('hairDyeAllergy', v)} error={errors.hairDyeAllergy}
        />
        {formData.hairDyeAllergy === 'yes' && (
          <TextField field={{ key: 'hairDyeAllergyExplain', label: 'If yes, please explain:' }} value={formData.hairDyeAllergyExplain} onChange={v => onChange('hairDyeAllergyExplain', v)} error={errors.hairDyeAllergyExplain} />
        )}
        <RadioGroup
          field={{ key: 'permAllergy', label: 'Have you ever had an allergic reaction to a perm?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.permAllergy} onChange={v => onChange('permAllergy', v)} error={errors.permAllergy}
        />
        {formData.permAllergy === 'yes' && (
          <TextField field={{ key: 'permAllergyExplain', label: 'If yes, please explain:' }} value={formData.permAllergyExplain} onChange={v => onChange('permAllergyExplain', v)} error={errors.permAllergyExplain} />
        )}
        <RadioGroup
          field={{ key: 'microbladed', label: 'Are your eyebrows microbladed?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.microbladed} onChange={v => onChange('microbladed', v)} error={errors.microbladed}
        />
        {formData.microbladed === 'yes' && (
          <TextField field={{ key: 'microbladedWhen', label: 'If yes, when did you get them done:' }} value={formData.microbladedWhen} onChange={v => onChange('microbladedWhen', v)} error={errors.microbladedWhen} />
        )}
      </div>

      {/* Medical History */}
      <div className="fr-section">
        <div className="fr-section-header">Medical History</div>
        <RadioGroup
          field={{ key: 'pregnant', label: 'Are you or could you be pregnant?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.pregnant} onChange={v => onChange('pregnant', v)} error={errors.pregnant}
        />
        <RadioGroup
          field={{ key: 'eyeInjury', label: 'Do you have, or are you being treated for any kind of eye injury?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.eyeInjury} onChange={v => onChange('eyeInjury', v)} error={errors.eyeInjury}
        />
        {formData.eyeInjury === 'yes' && (
          <TextField field={{ key: 'eyeInjuryExplain', label: 'If yes, please explain:' }} value={formData.eyeInjuryExplain} onChange={v => onChange('eyeInjuryExplain', v)} error={errors.eyeInjuryExplain} />
        )}
        <RadioGroup
          field={{ key: 'allergies', label: 'Do you have any allergies?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.allergies} onChange={v => onChange('allergies', v)} error={errors.allergies}
        />
        {formData.allergies === 'yes' && (
          <TextField field={{ key: 'allergiesExplain', label: 'If yes, please explain:' }} value={formData.allergiesExplain} onChange={v => onChange('allergiesExplain', v)} error={errors.allergiesExplain} />
        )}
        <RadioGroup
          field={{ key: 'medications', label: 'Are you currently taking any medications or supplements?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.medications} onChange={v => onChange('medications', v)} error={errors.medications}
        />
        {formData.medications === 'yes' && (
          <TextField field={{ key: 'medicationsExplain', label: 'If yes, please list:' }} value={formData.medicationsExplain} onChange={v => onChange('medicationsExplain', v)} error={errors.medicationsExplain} />
        )}
        <RadioGroup
          field={{ key: 'retinolAccutane', label: 'Are you currently using Retinol, Accutane, AHAs, or BHAs?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.retinolAccutane} onChange={v => onChange('retinolAccutane', v)} error={errors.retinolAccutane}
        />
        <CheckboxGroup
          field={{
            key: 'conditions', label: 'Do you have any of the following conditions? (Please check all that apply)', columns: true,
            options: [
              { value: 'alopecia', label: 'Alopecia' },
              { value: 'cancer', label: 'Cancer' },
              { value: 'cataract', label: 'Cataract' },
              { value: 'conjunctivitis', label: 'Conjunctivitis' },
              { value: 'diabetes', label: 'Diabetes' },
              { value: 'dryEyes', label: 'Dry Eyes' },
              { value: 'eczema', label: 'Eczema' },
              { value: 'glaucoma', label: 'Glaucoma' },
              { value: 'lupus', label: 'Lupus' },
              { value: 'psoriasisEyes', label: 'Psoriasis Around the Eyes' },
              { value: 'thyroid', label: 'Thyroid disease' },
              { value: 'recentEyeInfection', label: 'Recent Eye Infection' },
              { value: 'recentEyeSurgery', label: 'Recent Eye Surgery' },
              { value: 'sensitiveSkin', label: 'Sensitive Skin' },
              { value: 'sunburn', label: 'Sunburn' },
              { value: 'other', label: 'Other' },
              { value: 'none', label: 'None' }
            ]
          }}
          value={formData.conditions} onChange={v => onChange('conditions', v)} error={errors.conditions}
        />
        {formData.conditions?.other && (
          <TextField field={{ key: 'conditionsOther', label: 'Other:' }} value={formData.conditionsOther} onChange={v => onChange('conditionsOther', v)} error={errors.conditionsOther} />
        )}
        <TextField field={{ key: 'physicianTreatment', label: 'Please list any illness or condition you are currently being treated by a physician for:' }} value={formData.physicianTreatment} onChange={v => onChange('physicianTreatment', v)} error={errors.physicianTreatment} />
      </div>

      {/* Intake Consent + Signature */}
      <div className="fr-section">
        <ConsentTextSection paragraphs={[BROW_LAM_INTAKE_CONSENT]} title="By signing below, I agree to the following:" />
        <div className="fr-sig-row">
          <SignaturePad
            label="Signature"
            required
            value={formData.intakeSignature || ''}
            onChange={v => onChange('intakeSignature', v)}
          />
          <DateField
            field={{ key: 'intakeDate', label: 'Date', required: true, halfWidth: true }}
            value={formData.intakeDate}
            onChange={v => onChange('intakeDate', v)}
            error={errors.intakeDate}
          />
        </div>
        {errors.intakeSignature && <div className="fr-error">{errors.intakeSignature}</div>}
      </div>

      {/* Brow Lamination Consent Form */}
      <BrowLamConsentSection formData={formData} onChange={onChange} errors={errors} />
    </>
  );
}

function renderLaserForm(formData, onChange, errors, guestInfo) {
  return (
    <>
      <div className="fr-form-title">LASER MEDICAL HISTORY AND CONSENT FORM</div>

      {/* Personal Information */}
      <div className="fr-section">
        <div className="fr-section-header">Personal Information</div>
        <div className="fr-row">
          <TextField field={{ key: 'lastName', label: 'Last Name', required: true, halfWidth: true }} value={formData.lastName} onChange={v => onChange('lastName', v)} error={errors.lastName} />
          <TextField field={{ key: 'firstName', label: 'First Name', required: true, halfWidth: true }} value={formData.firstName} onChange={v => onChange('firstName', v)} error={errors.firstName} />
        </div>
        <TextField field={{ key: 'address', label: 'Address' }} value={formData.address} onChange={v => onChange('address', v)} error={errors.address} />
        <div className="fr-row">
          <TextField field={{ key: 'city', label: 'City', halfWidth: true }} value={formData.city} onChange={v => onChange('city', v)} error={errors.city} />
          <TextField field={{ key: 'province', label: 'Province', halfWidth: true }} value={formData.province} onChange={v => onChange('province', v)} error={errors.province} />
        </div>
        <div className="fr-row">
          <TextField field={{ key: 'postalCode', label: 'Postal Code', halfWidth: true }} value={formData.postalCode} onChange={v => onChange('postalCode', v)} error={errors.postalCode} />
          <TextField field={{ key: 'homePhone', label: 'Home Phone', halfWidth: true, inputType: 'tel' }} value={formData.homePhone} onChange={v => onChange('homePhone', v)} error={errors.homePhone} />
        </div>
        <div className="fr-row">
          <TextField field={{ key: 'mobilePhone', label: 'Mobile Phone', halfWidth: true, inputType: 'tel' }} value={formData.mobilePhone} onChange={v => onChange('mobilePhone', v)} error={errors.mobilePhone} />
          <DateField field={{ key: 'dob', label: 'Date of Birth', halfWidth: true }} value={formData.dob} onChange={v => onChange('dob', v)} error={errors.dob} />
        </div>
        <RadioGroup
          field={{ key: 'sex', label: 'Sex', options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }] }}
          value={formData.sex} onChange={v => onChange('sex', v)} error={errors.sex}
        />
        <TextField field={{ key: 'treatmentAreas', label: 'Which body area/areas would you like treated?' }} value={formData.treatmentAreas} onChange={v => onChange('treatmentAreas', v)} error={errors.treatmentAreas} />
      </div>

      {/* Laser History */}
      <div className="fr-section">
        <div className="fr-section-header">Laser History</div>
        <RadioGroup
          field={{ key: 'previousLaser', label: 'Have you had any previous laser hair removal sessions?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
          value={formData.previousLaser} onChange={v => onChange('previousLaser', v)} error={errors.previousLaser}
        />
        {formData.previousLaser === 'yes' && (
          <RadioGroup
            field={{ key: 'laserAdverseReaction', label: 'If yes, did you have any adverse reactions?', required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
            value={formData.laserAdverseReaction} onChange={v => onChange('laserAdverseReaction', v)} error={errors.laserAdverseReaction}
          />
        )}
        {formData.laserAdverseReaction === 'yes' && (
          <TextField field={{ key: 'laserAdverseExplain', label: 'If yes, please explain:' }} value={formData.laserAdverseExplain} onChange={v => onChange('laserAdverseExplain', v)} error={errors.laserAdverseExplain} />
        )}
      </div>

      {/* Medical Questions */}
      <div className="fr-section">
        <div className="fr-section-header">Medical History</div>
        {[
          { key: 'pregnant', label: 'Are you pregnant or planning to become pregnant?' },
          { key: 'breastfeeding', label: 'Are you breastfeeding?' },
          { key: 'skinConditions', label: 'Do you have any skin conditions (eczema, psoriasis, vitiligo, dermatitis)?' },
          { key: 'keloiding', label: 'Do you have a history of keloiding or abnormal scarring?' },
          { key: 'photosensitivity', label: 'Do you have a history of photosensitivity?' },
          { key: 'herpes', label: 'Do you have a history of herpes simplex (cold sores) in the treatment area?' },
          { key: 'cancer', label: 'Do you have or have you had skin cancer or any other type of cancer?' },
          { key: 'diabetes', label: 'Do you have diabetes?' },
          { key: 'autoimmune', label: 'Do you have any autoimmune disorders (lupus, etc.)?' },
          { key: 'seizures', label: 'Do you have epilepsy or a seizure disorder?' },
          { key: 'pacemaker', label: 'Do you have a pacemaker or any implanted electronic device?' },
          { key: 'bloodThinners', label: 'Are you taking blood-thinning medications?' },
          { key: 'accutane', label: 'Are you currently using or have you used Accutane (isotretinoin) in the past 6 months?' },
          { key: 'antibiotics', label: 'Are you currently taking antibiotics or photosensitizing medications?' },
          { key: 'retinoids', label: 'Are you using topical retinoids, AHAs, BHAs, or exfoliants on the treatment area?' },
          { key: 'tattoos', label: 'Do you have any tattoos in the treatment area?' },
          { key: 'recentSun', label: 'Have you had significant sun exposure or tanning in the last 2-4 weeks?' },
          { key: 'recentProcedures', label: 'Have you had any recent cosmetic procedures (chemical peel, microneedling, etc.) in the treatment area?' },
          { key: 'metalImplants', label: 'Do you have any metal implants or piercings in the treatment area?' },
          { key: 'otherConditions', label: 'Do you have any other medical conditions or are taking any medications not mentioned above?' }
        ].map(q => (
          <div key={q.key}>
            <RadioGroup
              field={{ key: q.key, label: q.label, required: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }}
              value={formData[q.key]} onChange={v => onChange(q.key, v)} error={errors[q.key]}
            />
            {formData[q.key] === 'yes' && (
              <TextField field={{ key: `${q.key}Explain`, label: 'Please list:' }} value={formData[`${q.key}Explain`]} onChange={v => onChange(`${q.key}Explain`, v)} error={errors[`${q.key}Explain`]} />
            )}
          </div>
        ))}
      </div>

      {/* Consent */}
      <div className="fr-section">
        <div className="fr-section-header">Consent</div>
        <ConsentTextSection paragraphs={[
          'I acknowledge that laser hair removal involves the use of concentrated light energy to target and damage hair follicles. I understand the potential risks, benefits, and alternatives to this procedure.',
          'I understand that results may vary and that multiple sessions are typically required. I agree to follow all pre- and post-treatment instructions provided.',
          'I certify that the information provided above is accurate and complete. I agree to inform the technician of any changes to my health, medications, or skin condition before each session.',
          'I voluntarily consent to laser hair removal treatment. I release Cutie Pie Wax Bar Inc., its employees, and contractors from any liability related to the treatment, except in cases of gross negligence.',
          'I understand that this consent is valid for all future laser hair removal sessions unless revoked in writing.'
        ]} />
        <RadioGroup
          field={{
            key: 'consentAcknowledge', label: 'I have read and understand the above consent', required: true,
            options: [{ value: 'agree', label: 'I acknowledge and agree' }]
          }}
          value={formData.consentAcknowledge} onChange={v => onChange('consentAcknowledge', v)} error={errors.consentAcknowledge}
        />
        <div className="fr-sig-row">
          <SignaturePad label="Signature" required value={formData.signature || ''} onChange={v => onChange('signature', v)} />
          <DateField field={{ key: 'signatureDate', label: 'Date', required: true, halfWidth: true }} value={formData.signatureDate} onChange={v => onChange('signatureDate', v)} error={errors.signatureDate} />
        </div>
        {errors.signature && <div className="fr-error">{errors.signature}</div>}
        <TextField field={{ key: 'printName', label: 'Print Name' }} value={formData.printName} onChange={v => onChange('printName', v)} error={errors.printName} />
        <div className="fr-sig-row">
          <SignaturePad label="Signature" required value={formData.signature2 || ''} onChange={v => onChange('signature2', v)} />
          <DateField field={{ key: 'signatureDate2', label: 'Date', required: true, halfWidth: true }} value={formData.signatureDate2} onChange={v => onChange('signatureDate2', v)} error={errors.signatureDate2} />
        </div>
        {errors.signature2 && <div className="fr-error">{errors.signature2}</div>}
      </div>
    </>
  );
}

// ─── REQUIRED FIELDS PER FORM ────────────────────────────────────

function getRequiredFields(formType, formData) {
  const required = [];
  if (formType === 'guest-intake') {
    required.push('firstName', 'lastName', 'birthday', 'email', 'retinol', 'agreeTerms', 'signature', 'signatureDate');
    if (!formData.servicesReceiving || !Object.values(formData.servicesReceiving).some(v => v)) {
      required.push('servicesReceiving');
    }
    if (!formData.howDidYouHear || !Object.values(formData.howDidYouHear).some(v => v)) {
      required.push('howDidYouHear');
    }
    if (formData.servicesReceiving?.waxing) {
      required.push('waxedBefore', 'postWaxExperiences');
    }
  } else if (formType === 'brow-lam') {
    required.push('firstName', 'lastName', 'browsLaminated', 'tintedBrows', 'hairDyeAllergy', 'permAllergy', 'microbladed');
    required.push('pregnant', 'eyeInjury', 'allergies', 'medications', 'retinolAccutane');
    required.push('intakeSignature', 'intakeDate', 'consentSignature', 'consentDate');
    for (let i = 0; i < BROW_LAM_CONSENT_STATEMENTS.length; i++) {
      required.push(`consent_${i}`);
    }
  } else if (formType === 'laser') {
    required.push('firstName', 'lastName', 'previousLaser', 'signature', 'signatureDate', 'signature2', 'signatureDate2', 'consentAcknowledge');
    const medicalKeys = ['pregnant', 'breastfeeding', 'skinConditions', 'keloiding', 'photosensitivity', 'herpes', 'cancer', 'diabetes', 'autoimmune', 'seizures', 'pacemaker', 'bloodThinners', 'accutane', 'antibiotics', 'retinoids', 'tattoos', 'recentSun', 'recentProcedures', 'metalImplants', 'otherConditions'];
    required.push(...medicalKeys);
  }
  return required;
}

// ─── FORM NAME MATCHING ─────────────────────────────────────────

const FORM_NAME_MAP = {
  'guest form': 'guest-intake',
  'guest intake': 'guest-intake',
  'client intake': 'guest-intake',
  'new client': 'guest-intake',
  'brow lamination client intake': 'brow-lam',
  'brow lamination intake': 'brow-lam',
  'laser medical history': 'laser',
  'laser hair removal': 'laser',
};

export function matchFormType(formName) {
  if (!formName) return null;
  const lower = formName.toLowerCase();
  for (const [pattern, type] of Object.entries(FORM_NAME_MAP)) {
    if (lower.includes(pattern)) return type;
  }
  return null;
}

// ─── MAIN FORM RENDERER ─────────────────────────────────────────

function FormRenderer({ formType, guestInfo, formMeta, onComplete, onBack }) {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formRef = useRef(null);

  // Auto-fill guest info
  useEffect(() => {
    const initial = {};
    if (guestInfo) {
      initial.firstName = guestInfo.firstName || '';
      initial.lastName = guestInfo.lastName || '';
      initial.email = guestInfo.email || '';
      if (guestInfo.dob) initial.birthday = guestInfo.dob;
      if (guestInfo.phone) initial.mobilePhone = guestInfo.phone;
      // Auto-fill today's date for signature dates
      const today = new Date().toISOString().split('T')[0];
      initial.signatureDate = today;
      initial.signatureDate2 = today;
      initial.intakeDate = today;
      initial.consentDate = today;
    }
    setFormData(initial);
    setErrors({});
    setSubmitError('');
  }, [formType, guestInfo]);

  function handleChange(key, value) {
    setFormData(prev => ({ ...prev, [key]: value }));
    setErrors(prev => {
      const e = { ...prev };
      delete e[key];
      return e;
    });
    setSubmitError('');
  }

  function validate() {
    const errs = {};
    const requiredFields = getRequiredFields(formType, formData);

    for (const key of requiredFields) {
      const val = formData[key];
      if (val === undefined || val === null || val === '' || val === false) {
        errs[key] = 'This field is required';
      }
      // Checkbox groups: at least one must be checked
      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        if (!Object.values(val).some(v => v)) {
          errs[key] = 'Please select at least one option';
        }
      }
    }
    return errs;
  }

  async function handleSubmit() {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error
      const firstErrorKey = Object.keys(validationErrors)[0];
      const el = formRef.current?.querySelector(`[data-field="${firstErrorKey}"], .fr-error`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch(`${API_URL}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: guestInfo?.guestId,
          formType,
          formData,
          formMeta // includes form_id, owner_id, appointment_id
        })
      });
      const data = await res.json();
      if (data.success) {
        onComplete();
      } else {
        setSubmitError(data.error || 'Failed to submit form. Please try again.');
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  }

  const renderers = {
    'guest-intake': renderGuestIntakeForm,
    'brow-lam': renderBrowLamForm,
    'laser': renderLaserForm
  };

  const renderForm = renderers[formType];
  if (!renderForm) {
    return (
      <div className="fr-wrapper">
        <div className="fr-unsupported">
          <p>This form type is not yet supported in our custom form system.</p>
          {formMeta?.formUrl && (
            <a href={formMeta.formUrl} target="_blank" rel="noopener noreferrer" className="fr-fallback-link">
              Open form in Zenoti
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fr-wrapper" ref={formRef}>
      <div className="fr-logo">
        <img src="/kiosk-logo.png" alt="Cutie Pie Wax Bar" className="fr-logo-img" />
      </div>

      {renderForm(formData, handleChange, errors, guestInfo)}

      {submitError && (
        <div className="fr-submit-error">{submitError}</div>
      )}

      <div className="fr-actions">
        <button
          className="fr-submit-btn"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'SUBMITTING...' : 'SUBMIT FORM'}
        </button>
        <button className="fr-back-btn" onClick={onBack} disabled={submitting}>
          Back to forms
        </button>
      </div>
    </div>
  );
}

export default FormRenderer;
